/**
 * Groups flat test executions into "boxes".
 *
 * A box = one logical test run (for a period/config). Re-runs are chained via
 * supersedesExecutionId — every execution that (transitively) supersedes a root
 * belongs in the same box. Each box lists its attempts newest-first.
 *
 * Returns: [{ rootId, attempts: [exec…], current: exec, period…, status… }]
 */
export function groupTestRuns(runs) {
  if (!runs || runs.length === 0) return [];

  // Map executionId → run
  const byId = {};
  runs.forEach(r => { byId[r.executionId] = r; });

  // Find the root of a supersedes chain for a given run
  const rootOf = (run) => {
    let cur = run;
    const seen = new Set();
    while (cur?.supersedesExecutionId && byId[cur.supersedesExecutionId] && !seen.has(cur.executionId)) {
      seen.add(cur.executionId);
      cur = byId[cur.supersedesExecutionId];
    }
    return cur;
  };

  // Group by root executionId
  const groups = {};
  runs.forEach(run => {
    const root = rootOf(run);
    const key = root.executionId;
    if (!groups[key]) groups[key] = [];
    groups[key].push(run);
  });

  // Build box objects
  const boxes = Object.entries(groups).map(([rootId, attempts]) => {
    // Newest attempt first (highest test_run_number, else latest created)
    const sorted = [...attempts].sort((a, b) => {
      const an = a.test_run_number ?? 0;
      const bn = b.test_run_number ?? 0;
      if (an !== bn) return bn - an;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    const current = sorted.find(a => a.isCurrent) || sorted[0];
    return {
      rootId,
      attempts: sorted,
      current,
      status: current.testStatus || current.test_status || current.status || 'pending',
    };
  });

  // Newest box first (by current attempt's created date / id)
  boxes.sort((a, b) => {
    const ad = new Date(a.current.created_at || 0);
    const bd = new Date(b.current.created_at || 0);
    if (bd - ad !== 0) return bd - ad;
    return String(b.rootId).localeCompare(String(a.rootId));
  });

  return boxes;
}

/** Map a test_status to the wizard step to open (this wizard has 3 steps). */
export function resumeStepForStatus(status, run) {
  const pop = run?.population_data || {};
  const hasSamples = !!(
    (pop.selected_samples && pop.selected_samples.length > 0)
    || pop.sample_size
    || run?.total_sample_size
  );
  if (!hasSamples && run?.selected_periods) return 3;
  switch ((status || '').toLowerCase()) {
    case 'period_selected':
      return 3;
    case 'population_validated':
    case 'population_configured':
      return 3;
    case 'draft':
    default:
      return run?.selected_periods ? 3 : 1;
  }
}
